import { useEffect, useState } from "react";
import { useMsal, useAccount } from "@azure/msal-react";
import SessionExpired from "../components/ui/kit-mdtw/modals/session/SessionExpired";
import { loginRequest } from "../config/authConfig";
import {
  AccountInfo,
  CacheLookupPolicy,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { msalInstance } from "..";

export default () => {
  const tiempoCuentaRegresiva: number = 59;
  const [lastInteraction, setLastInteraction] = useState<Date>(new Date());
  const { instance } = useMsal();
  const [isInvalid, setIsInvalid] = useState(false);
  const [token, setToken] = useState<any>(null);
  const [intervalTime, setIntervalTime] = useState<any>();
  const [mouseStop, setMouseStop] = useState<boolean>(false);
  const [mouseInactive, setMouseInactive] = useState<boolean>(false);
  const [isOpenModalSessionTimeOut, setIsOpenModalSessionTimeOut] =
    useState<boolean>(false);
  const [isOpenModalCandidate, setIsOpenModalCandidate] =
    useState<boolean>(false);
  const [contador, setContador] = useState<number>(tiempoCuentaRegresiva);

  // se escucha los eventos
  useEffect(() => {
    window.addEventListener("blur", handleEventBlur);
    window.addEventListener("focus", handleEventFocus);
    return () => {
      window.removeEventListener("blur", handleEventBlur);
      window.removeEventListener("focus", handleEventFocus);
    };
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("keypress", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("keypress", handleMouseMove);
    };
  }, []);

  const handleMouseMove = () => {
    setLastInteraction(new Date());
    setMouseStop(false);
  };

  // Verifica si la sesión sigue siendo válida silenciosamente.
  // true  → MSAL refrescó/devolvió token sin interacción → la sesión está sana
  // false → MSAL lanzó InteractionRequiredAuthError → se necesita re-login real
  const isSessionStillValid = async (): Promise<boolean> => {
    const account = msalInstance.getActiveAccount();
    if (!account) return false;
    try {
      const response = await msalInstance.acquireTokenSilent({
        scopes: ["User.Read"],
        account,
        forceRefresh: false,
      });
      if (response?.idToken) {
        sessionStorage.setItem("token", response.idToken);
      }
      return true;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) return false;
      // Errores transitorios (red, race condition de MSAL) → no molestar al usuario
      return true;
    }
  };

  // Listener de visibilitychange: refresca el token al volver a la pestaña
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;
      const valid = await isSessionStillValid();
      if (!valid) setIsOpenModalSessionTimeOut(true);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Refresco de token: siempre activo, independiente de mouseStop
  useEffect(() => {
    const intervalToken = setInterval(() => {
      refreshToken();
    }, 200000); // miliIntervalTokenRefresh
    return () => clearInterval(intervalToken);
  }, []);

  // Timeout de inactividad: solo depende de mouseStop
  useEffect(() => {
    const modal = document.getElementById("ModalReclutarCandidato");
    if (modal?.style.display == "flex") {
      setIsOpenModalCandidate(true);
      return;
    }
    setIsOpenModalCandidate(false);
    if (!mouseStop) return;
    const setSessionTimeOut = setTimeout(async () => {
      const valid = await isSessionStillValid();
      if (!valid) {
        setIsOpenModalSessionTimeOut(true);
      } else {
        // Sesión sana → resetea el estado de inactividad en silencio
        setMouseStop(false);
        setLastInteraction(new Date());
        setContador(tiempoCuentaRegresiva);
      }
    }, 500000); // miliSessionTimeOut
    return () => clearTimeout(setSessionTimeOut);
  }, [mouseStop, isOpenModalCandidate]);

  const handleEventBlur = () => {
    setMouseStop(true);
  };

  const handleEventFocus = () => {
    setIntervalTime(clearTimeout(intervalTime));
    clearTimeout(intervalTime);
    setMouseStop(false);
  };

  const tokenBroken = () => {
    localStorage.clear();
    sessionStorage.clear();
  };

  const refreshToken = async () => {
    const currentAccount = instance.getActiveAccount();
    if (!currentAccount) return;

    const silentRequest = {
      scopes: ["User.Read"],
      account: currentAccount,
      forceRefresh: true,
      cacheLookupPolicy: CacheLookupPolicy.Default,
    };

    const request = {
      ...loginRequest,
      loginHint: currentAccount.username,
    };

    try {
      const response = await msalInstance.acquireTokenSilent(silentRequest);
      sessionStorage.setItem("token", response.idToken);
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        try {
          const popupResponse = await msalInstance.acquireTokenPopup(request);
          if (popupResponse?.idToken) {
            sessionStorage.setItem("token", popupResponse.idToken);
          }
        } catch (popupError) {
          if (popupError instanceof InteractionRequiredAuthError) {
            await msalInstance.acquireTokenRedirect(request);
          }
        }
      }
    }

    setIsInvalid(false);
    localStorage.removeItem("closeAccept");
  };

  const handleLogout = () => {
    msalInstance.logoutRedirect();
    msalInstance.logoutPopup();
    setIsOpenModalSessionTimeOut(false);
    setMouseInactive(false);
  };

  const handleEventTokenExtend = () => {
    setIsOpenModalSessionTimeOut(false);
    refreshToken();
    setContador(tiempoCuentaRegresiva);
    setMouseInactive(false);
  };

  return (
    <>
      {isOpenModalSessionTimeOut && (
        <>
          {contador == 0 ? (
            <>
              {tokenBroken()}
              <SessionExpired
                title="Su sesión expiró"
                message="Vuelve a iniciar sesión para continuar."
                onChangeBack={handleLogout}
                onChangeGo={handleLogout}
              />
            </>
          ) : (
            <SessionExpired
              title="Su sesión está a punto de expirar"
              subTitle="Por seguridad tu sesión se cerrará en:"
              message="Para mantenerse conectado, presione continuar."
              onChangeBack={handleLogout}
              onChangeGo={handleEventTokenExtend}
              titleButtonGo="Continuar"
              contador={contador}
              setContador={setContador}
            />
          )}
        </>
      )}
    </>
  );
};
